import React, { useState, useCallback, useEffect, useRef } from "react";
import {
    Typography,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Collapse,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    Pagination,
    PaginationItem,
    Box
} from "@mui/material";
import { useLocation } from "react-router-dom";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "../assets/icons/ExpandMoreIcon.png";
import MinusIcon from "../assets/icons/MinusIcons.png";
import uploadIcon from "../assets/icons/uploadIcon.png";
import { fetchBulkCVEs, fetchCVE } from "../actions/epssAction.js";

import leftArrowIcon from "../assets/icons/leftArrowIcon.png";
import downArrowIcon from "../assets/icons/downArrowIcon.png";
import {
    MainContentBox, CveTitleText, SingleCveScoreContainer, ButtonGroupStyled,
    FetchButton,
    IconWrapper,
    InputField,
    StyledButton,
} from "../assets/styles/singlecveStyledComponent";
import {
    CenteredTableContainer,
    StyledTable,
    StyledTableHeadCell,
    StyledTableBodyCell,
    ToggleIconButton,
    BulkCustomAccordionDetails,
    BulkCustomTypography,
    BulkCustomAccordionSummary,
    BulkCustomAccordion,
    IconImage,
    BulkUploadInputLabel,
    DownloadLinkLabel,
    UploadButton,
    DownloadLinks,
    BulkScoreContainer,
    ReferencesContainer,
    ReferenceItem,
    ReferenceLink,
    AccordionSummaryValues,
    StyledTableRow,
    SingleCveErrorText,
    BulkCveErrorText,
    NoUnderlineLink,
} from "../assets/styles/bulkCveStyledComponents.js";

import { POST_API } from "../utils/api.js";
import { API_DOMAIN } from "../utils/domain.js";

const Reports = () => {

    const [cveError, setCveError] = useState("");
    const [formData, setFormData] = useState({
        vendor: "",
        product: "",
    });
    const [loading, setLoading] = useState({
        vendors: false,
        vendorData: false,
        fetchingCVE: false,
        apiResponse: false
    });

    const [resultData, setResultData] = useState()

    const ITEMS_PER_PAGE = 10;
    const [page, setPage] = useState(1);
    const allResults = resultData?.vulnerabilities?.results || [];
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedResults = allResults.slice(startIndex, endIndex);
    const totalPages = Math.ceil(allResults.length / ITEMS_PER_PAGE);

    const location = useLocation();

    // Helper function to format attribute names
    const formatAttributeName = (key) => {
        return key
            .replace(/_/g, ' ') // Replace underscores with spaces
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };



    const [state, setState] = useState({
        tabValue: 0,
        cve_id: "",
        singleCveResponse: null,
        bulkCveResponse: null,
        expanded: false,
        expandedIndex: null,
        error: "",
        singleCveExpandedIndex: [0, 1, 2, 3, 4, 5, 6, 7, 8],
        showScoreContainer: false,
        isBulkFetching: false,
        openSections: {},
        expandedAccordions: {},
        selectedFile: null,
        singleCveError: "",
        bulkCveError: "",
        isUserLoggedIn: false
    });
    const hasFetched = useRef(false);

    useEffect(() => {

        setLoading((prev) => ({ ...prev, apiResponse: true }));


        if (!location.search) return;
        if (hasFetched.current) return;
        hasFetched.current = true;

        fetchCVEs();

    }, [location.search])

    const fetchCVEs = async () => {
        try {
            const params = new URLSearchParams(location.search);
            const email = params.get("email");
            const brand = params.get("brand");
            const product = params.get("product");

            const requestBody = {
                vendorName: brand,
                productName: product,
                outputFormat: "json",
                // pageNumber: 1,
                resultsPerPage: 20,
            };

            console.log("Fetching vulnerabilities with:", requestBody);

            const result = await POST_API(`${API_DOMAIN}/api/v1/cvedetails/vulnerabilities/search`, requestBody);
            console.log("Vulnerabilities API response:", result);

            if (result.success && result.data) {
                setResultData(result?.data)
                setLoading((prev) => ({ ...prev, apiResponse: false }));



                // Check for rate limit error
                if (result.data.errors && result.data.errors.error === "Rate limit exceeded") {
                    const errorMessage = result.data.errors.error_description || "Rate limit exceeded. Please try again in a minute.";
                    console.error("Rate limit error:", errorMessage);
                    setCveError(errorMessage);
                    return;
                }

                const productScore = result.data.productScore || 0;
                const vulnerabilities = result.data.vulnerabilities?.results || [];

                const tier = getRiskTier(productScore);
                const scoreData = {
                    riskScoreValue: productScore,
                    riskTier: tier,
                    totalCVEs: vulnerabilities.length,
                    cveDetails: vulnerabilities,
                    vendor: brand,
                    product: product,
                };

                console.log("Product Score:", productScore);
                console.log("Total Vulnerabilities:", vulnerabilities.length);
                console.log("Risk Tier:", tier);
            } else {
                setCveError("Failed to fetch vulnerability data");
            }
        } catch (error) {
            console.error("Error fetching vulnerabilities:", error);
            setCveError("An error occurred while fetching vulnerability data");
        } finally {
            setLoading((prev) => ({ ...prev, fetchingCVE: false }));
            setLoading((prev) => ({ ...prev, apiResponse: false }));

        }

    }


    const getRiskTier = (score) => {
        if (score >= 81) {
            return { text: 'High Risk', level: 'high' };
        } else if (score >= 61) {
            return { text: 'Significant Risk', level: 'significant' };
        } else if (score >= 41) {
            return { text: 'Moderate Risk', level: 'moderate' };
        } else {
            return { text: 'Low Risk', level: 'low' };
        }
    };


    const handleBulkAccordionChange = useCallback((index, key) => {
        setState((prevState) => ({
            ...prevState,
            expandedAccordions: {
                ...prevState.expandedAccordions,
                [index]: {
                    ...prevState.expandedAccordions[index],
                    [key]: !prevState.expandedAccordions[index]?.[key],
                },
            },
        }));
    }, []);


    const handleExpandClick = (index) => {
        setState((prevState) => {
            const newExpandedIndex = prevState.expandedIndex === index ? null : index;

            return {
                ...prevState,
                expandedIndex: newExpandedIndex,
                expandedAccordions: {
                    ...prevState.expandedAccordions,
                    [index]:
                        newExpandedIndex !== null
                            ? {
                                severity: true,
                                summary: true,
                                references: true,
                                migration_patches: true,
                                exploits: true,
                                vector: true,
                                cwe_id: true,
                            }
                            : {},
                },
            };
        });
    };

    const handlePaginationChange = (event, value) => {
        setPage(value)
        setState((prev) => ({
            ...prev,
            expandedIndex: null, // collapse open row when page changes
        }));


    }


    const CVEReportData = [
        {
            "cve_id": "CVE-2019-1010022",
            "severity": null,
            "highest_severity_score": "No score",
            "severity_score": "N/A",
            "summary": "GNU Libc current is affected by: Mitigation bypass. The impact is: Attacker may bypass stack guard protection. The component is: nptl. The attack vector is: Exploit stack buffer overflow vulnerability and use this bypass vulnerability to bypass stack guard. NOTE: Upstream comments indicate \"this is being treated as a non-security bug and no real threat.",
            "references": [
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://sourceware.org/bugzilla/show_bug.cgi?id=22850"
                },
                {
                    "name": "CVE-2019-1010022",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_DEBIAN"
                    ],
                    "url": "https://security-tracker.debian.org/tracker/CVE-2019-1010022"
                },
                {
                    "name": "CVE-2019-1010022",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_UBUNTU"
                    ],
                    "url": "https://ubuntu.com/security/CVE-2019-1010022"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://sourceware.org/bugzilla/show_bug.cgi?id=22850#c3"
                }
            ],
            "mitigation_and_patches": null,
            "exploits": null,
            "metasploit_payload": {
                "orgId": "af854a3a-2127-422b-91ae-364da2661108",
                "shortName": "CVE",
                "dateUpdated": "2024-08-05T03:07:18.071Z"
            },
            "vector": null,
            "cwe_id": null,
            "epss": 0.001
        },
        {
            "cve_id": "CVE-2019-8457",
            "severity": null,
            "highest_severity_score": "No score",
            "severity_score": "N/A",
            "summary": "SQLite3 from 3.6.0 to and including 3.27.2 is vulnerable to heap out-of-bound read in the rtreenode() function when handling invalid rtree tables.",
            "references": [
                {
                    "name": "USN-4004-1",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_UBUNTU"
                    ],
                    "url": "https://usn.ubuntu.com/4004-1/"
                },
                {
                    "name": "USN-4004-2",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_UBUNTU"
                    ],
                    "url": "https://usn.ubuntu.com/4004-2/"
                },
                {
                    "name": "USN-4019-1",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_UBUNTU"
                    ],
                    "url": "https://usn.ubuntu.com/4019-1/"
                },
                {
                    "name": "USN-4019-2",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_UBUNTU"
                    ],
                    "url": "https://usn.ubuntu.com/4019-2/"
                },
                {
                    "name": "openSUSE-SU-2019:1645",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_SUSE"
                    ],
                    "url": "http://lists.opensuse.org/opensuse-security-announce/2019-06/msg00074.html"
                },
                {
                    "name": "FEDORA-2019-02b81266b7",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_FEDORA"
                    ],
                    "url": "https://lists.fedoraproject.org/archives/list/package-announce%40lists.fedoraproject.org/message/SJPFGA45DI4F5MCF2OAACGH3HQOF4G3M/"
                },
                {
                    "name": "FEDORA-2019-3377813d18",
                    "tags": [
                        "vendor-advisory",
                        "x_refsource_FEDORA"
                    ],
                    "url": "https://lists.fedoraproject.org/archives/list/package-announce%40lists.fedoraproject.org/message/OPKYSWCOM3CL66RI76TYVIG6TJ263RXH/"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.oracle.com/security-alerts/cpuapr2020.html"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.oracle.com/security-alerts/cpujul2020.html"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.oracle.com/technetwork/security-advisory/cpuoct2019-5072832.html"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.oracle.com/security-alerts/cpujan2020.html"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.sqlite.org/src/info/90acdbfce9c08858"
                },
                {
                    "tags": [
                        "x_refsource_MISC"
                    ],
                    "url": "https://www.sqlite.org/releaselog/3_28_0.html"
                },
                {
                    "tags": [
                        "x_refsource_CONFIRM"
                    ],
                    "url": "https://security.netapp.com/advisory/ntap-20190606-0002/"
                },
                {
                    "tags": [
                        "x_refsource_CONFIRM"
                    ],
                    "url": "https://kc.mcafee.com/corporate/index?page=content&id=SB10365"
                }
            ],
            "mitigation_and_patches": null,
            "exploits": null,
            "metasploit_payload": {
                "orgId": "af854a3a-2127-422b-91ae-364da2661108",
                "shortName": "CVE",
                "dateUpdated": "2024-08-04T21:17:31.585Z"
            },
            "vector": null,
            "cwe_id": "CWE-125",
            "epss": 0.03
        },
        {
            "cve_id": "CVE-2022-0563",
            "severity": null,
            "highest_severity_score": "No score",
            "severity_score": "N/A",
            "summary": "A flaw was found in the util-linux chfn and chsh utilities when compiled with Readline support. The Readline library uses an \"INPUTRC\" environment variable to get a path to the library config file. When the library cannot parse the specified file, it prints an error message containing data from the file. This flaw allows an unprivileged user to read root-owned files, potentially leading to privilege escalation. This flaw affects util-linux versions prior to 2.37.4.",
            "references": [
                {
                    "url": "https://lore.kernel.org/util-linux/20220214110609.msiwlm457ngoic6w%40ws.net.home/T/#u"
                },
                {
                    "url": "https://security.netapp.com/advisory/ntap-20220331-0002/"
                },
                {
                    "name": "GLSA-202401-08",
                    "tags": [
                        "vendor-advisory"
                    ],
                    "url": "https://security.gentoo.org/glsa/202401-08"
                }
            ],
            "mitigation_and_patches": null,
            "exploits": null,
            "metasploit_payload": {
                "orgId": "af854a3a-2127-422b-91ae-364da2661108",
                "shortName": "CVE",
                "dateUpdated": "2024-08-02T23:32:46.398Z"
            },
            "vector": null,
            "cwe_id": "CWE-209",
            "epss": 0.0002
        },
        {
            "cve_id": "CVE-2017-9117",
            "severity": "MEDIUM",
            "highest_severity_score": 4,
            "severity_score": 4,
            "summary": "In LibTIFF 4.0.6 and possibly other versions, the program processes BMP images without verifying that biWidth and biHeight in the bitmap-information header match the actual input, as demonstrated by a heap-based buffer over-read in bmp2tiff. NOTE: mentioning bmp2tiff does not imply that the activation point is in the bmp2tiff.c file (which was removed before the 4.0.7 release).",
            "references": [
                {
                    "name": "USN-3606-1",
                    "tags": [
                        "vendor-advisory"
                    ],
                    "url": "https://usn.ubuntu.com/3606-1/"
                },
                {
                    "name": "98581",
                    "tags": [
                        "vdb-entry"
                    ],
                    "url": "http://www.securityfocus.com/bid/98581"
                },
                {
                    "url": "http://bugzilla.maptools.org/show_bug.cgi?id=2690"
                },
                {
                    "url": "https://gitlab.com/libtiff/libtiff/-/issues/89"
                }
            ],
            "mitigation_and_patches": null,
            "exploits": null,
            "metasploit_payload": {
                "orgId": "af854a3a-2127-422b-91ae-364da2661108",
                "shortName": "CVE",
                "dateUpdated": "2024-08-05T16:55:22.239Z"
            },
            "vector": "CVSS:3.1/AV:L/AC:L/PR:N/UI:N/S:U/C:N/I:N/A:L",
            "cwe_id": "CWE-125",
            "epss": 0.001
        },
        {
            "cve_id": "CVE-2022-3570",
            "severity": "HIGH",
            "highest_severity_score": 7.7,
            "severity_score": 7.7,
            "summary": "Multiple heap buffer overflows in tiffcrop.c utility in libtiff library Version 4.4.0 allows attacker to trigger unsafe or out of bounds memory access via crafted TIFF image file which could result into application crash, potential information disclosure or any other context-dependent impact",
            "references": [
                {
                    "url": "https://gitlab.com/libtiff/libtiff/-/issues/381"
                },
                {
                    "url": "https://gitlab.com/libtiff/libtiff/-/issues/386"
                },
                {
                    "url": "https://gitlab.com/libtiff/libtiff/-/commit/bd94a9b383d8755a27b5a1bc27660b8ad10b094c"
                },
                {
                    "url": "https://gitlab.com/gitlab-org/cves/-/blob/master/2022/CVE-2022-3570.json"
                },
                {
                    "name": "[debian-lts-announce] 20230120 [SECURITY] [DLA 3278-1] tiff security update",
                    "tags": [
                        "mailing-list"
                    ],
                    "url": "https://lists.debian.org/debian-lts-announce/2023/01/msg00018.html"
                },
                {
                    "name": "DSA-5333",
                    "tags": [
                        "vendor-advisory"
                    ],
                    "url": "https://www.debian.org/security/2023/dsa-5333"
                },
                {
                    "url": "https://security.netapp.com/advisory/ntap-20230203-0002/"
                }
            ],
            "mitigation_and_patches": null,
            "exploits": null,
            "metasploit_payload": {
                "orgId": "af854a3a-2127-422b-91ae-364da2661108",
                "shortName": "CVE",
                "dateUpdated": "2024-08-03T01:14:02.491Z"
            },
            "vector": "CVSS:3.1/AV:L/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:H",
            "cwe_id": null,
            "epss": 0.0001
        }
    ]

    const DETAIL_FIELDS = [
        { key: "summary", label: "Summary" },
        { key: "references", label: "References" }
    ];

    return loading.apiResponse ? (
        <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "20px"
        }}>
            Loading...
        </div>
    ) : (
        <div>
            <>
                <CenteredTableContainer>
                    <StyledTable>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableHeadCell first>CVE ID</StyledTableHeadCell>
                                <StyledTableHeadCell last>CVSS Score</StyledTableHeadCell>
                                <StyledTableHeadCell middle>EPSS Score</StyledTableHeadCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {
                                paginatedResults?.map((data, resultIndex) => {
                                    const index = startIndex + resultIndex;
                                    return (
                                        data?.cvedetails && (
                                            <React.Fragment key={index}>
                                                <TableRow
                                                    onClick={() => handleExpandClick(index)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <StyledTableBodyCell
                                                        isFirst={true}
                                                        bold={state.expandedIndex === index}
                                                        noBottomBorder={state.expandedIndex === index}
                                                        isLast={index === state?.bulkCveResponse?.length - 1 || null}
                                                        expandedIndex={state.expandedIndex}
                                                        index={index}
                                                    >
                                                        {data?.cvedetails.cve_id}
                                                    </StyledTableBodyCell>

                                                    <StyledTableBodyCell
                                                        bold={state.expandedIndex === index}
                                                        noBottomBorder={state.expandedIndex === index}
                                                        isLast={index === state?.bulkCveResponse?.length - 1 || null}
                                                        expandedIndex={state.expandedIndex}
                                                        index={index}
                                                    >
                                                        {data?.cvedetails.severity_score}
                                                    </StyledTableBodyCell>

                                                    <StyledTableBodyCell
                                                        bold={state.expandedIndex === index}
                                                        noBottomBorder={state.expandedIndex === index}
                                                        isLast={
                                                            true || index === state?.bulkCveResponse?.length - 1
                                                        }
                                                        expandedIndex={state.expandedIndex}
                                                        index={index}
                                                    >
                                                        {data?.cvedetails.epss}
                                                    </StyledTableBodyCell>

                                                    <StyledTableBodyCell isToggleColumn={true}>
                                                        <ToggleIconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleExpandClick(index);
                                                            }}
                                                        >
                                                            <IconImage
                                                                src={
                                                                    state.expandedIndex === index
                                                                        ? downArrowIcon
                                                                        : leftArrowIcon
                                                                }
                                                                alt="Toggle"
                                                            />
                                                        </ToggleIconButton>
                                                    </StyledTableBodyCell>

                                                </TableRow>
                                                {
                                                    Object.entries(data?.cvedetails)
                                                        .filter(([key]) => !['cve', 'cve_id', 'epss', 'date', 'percentile'].includes(key))
                                                        .map(([key, value]) => (
                                                            value &&
                                                            <TableRow>
                                                                <TableCell
                                                                    colSpan={3}
                                                                    style={{ paddingBottom: 0, paddingTop: 0 }}
                                                                >
                                                                    <Collapse
                                                                        in={state.expandedIndex === index}
                                                                        timeout="auto"
                                                                        unmountOnExit
                                                                    >
                                                                        {/* {
                                                    Object.entries(cveData)
                                                } */}
                                                                        {/* <span>{cveData?.references}</span> */}
                                                                        <BulkCustomAccordion
                                                                            key={data?.cvedetails}
                                                                            expanded={
                                                                                state.expandedAccordions[index]?.[key] ||
                                                                                false
                                                                            }
                                                                            onChange={() =>
                                                                                handleBulkAccordionChange(index, key)
                                                                            }
                                                                        >
                                                                            <BulkCustomAccordionSummary
                                                                                expandIcon={
                                                                                    <IconWrapper
                                                                                        src={
                                                                                            state.expandedAccordions[index]?.[key]
                                                                                                ? MinusIcon
                                                                                                : ExpandMoreIcon
                                                                                        }
                                                                                        alt="Toggle"
                                                                                    />
                                                                                }
                                                                            >
                                                                                <BulkCustomTypography
                                                                                    expanded={
                                                                                        state.expandedAccordions[index]?.[key]
                                                                                    }
                                                                                >
                                                                                    {/* {cveData?.summary && cveData?.references} */}
                                                                                    <Tooltip title={formatAttributeName(key)} placement="top">
                                                                                        {formatAttributeName(key)}
                                                                                    </Tooltip>
                                                                                </BulkCustomTypography>
                                                                            </BulkCustomAccordionSummary>
                                                                            <BulkCustomAccordionDetails>
                                                                                {Array.isArray(value) ? (
                                                                                    <ReferencesContainer>
                                                                                        {value?.map(
                                                                                            (ref, idx) =>
                                                                                                ref.url && (
                                                                                                    <ReferenceItem key={idx}>
                                                                                                        <ReferenceLink
                                                                                                            href={ref?.url}
                                                                                                            target="_blank"
                                                                                                            rel="noopener noreferrer"
                                                                                                        >
                                                                                                            {ref?.url}
                                                                                                        </ReferenceLink>
                                                                                                    </ReferenceItem>
                                                                                                )
                                                                                        )}
                                                                                    </ReferencesContainer>
                                                                                ) : Array.isArray(value) ? (
                                                                                    <div>
                                                                                        {value?.map((exploit, idx) => (
                                                                                            <AccordionSummaryValues
                                                                                                key={idx}
                                                                                                variant="body2"
                                                                                            >
                                                                                                {exploit?.value}
                                                                                            </AccordionSummaryValues>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : typeof value === "object" && value !== null ? (

                                                                                    Object.entries(value).map(([k, v]) => (
                                                                                        <AccordionSummaryValues key={k}>
                                                                                            <strong>{formatAttributeName(k)}:</strong> {v ?? "—"}
                                                                                            <br />
                                                                                        </AccordionSummaryValues>
                                                                                    ))

                                                                                )
                                                                                    : (
                                                                                        <AccordionSummaryValues>
                                                                                            {value}
                                                                                        </AccordionSummaryValues>
                                                                                    )}

                                                                                {/* <AccordionSummaryValues>
                                                                        {cveData?.summary}
                                                                    </AccordionSummaryValues> */}

                                                                            </BulkCustomAccordionDetails>
                                                                        </BulkCustomAccordion>
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                }

                                                {/* <TableRow
                    //   onClick={() => handleExpandClick(index)}
                      style={{ cursor: "pointer" }}
                    >
                      <StyledTableBodyCell
                        isFirst={true}
                        bold={state.expandedIndex === index}
                        noBottomBorder={state.expandedIndex === index}
                        isLast={index === state.bulkCveResponse.length - 1}
                        expandedIndex={state.expandedIndex}
                        index={index}
                      >
                        {cveData.cve_id}
                      </StyledTableBodyCell>

                      <StyledTableBodyCell
                        bold={state.expandedIndex === index}
                        noBottomBorder={state.expandedIndex === index}
                        isLast={index === state.bulkCveResponse.length - 1}
                        expandedIndex={state.expandedIndex}
                        index={index}
                      >
                        {cveData.severity_score}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell
                        bold={state.expandedIndex === index}
                        noBottomBorder={state.expandedIndex === index}
                        isLast={
                          true || index === state.bulkCveResponse.length - 1
                        }
                        expandedIndex={state.expandedIndex}
                        index={index}
                      >
                        {cveData.epss}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell isToggleColumn={true}>
                        <ToggleIconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandClick(index);
                          }}
                        >
                          <IconImage
                            src={
                              state.expandedIndex === index
                                ? downArrowIcon
                                : leftArrowIcon
                            }
                            alt="Toggle"
                          />
                        </ToggleIconButton>
                      </StyledTableBodyCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={3}
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={state.expandedIndex === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          {[
                            {
                              key: "severity",
                              label: "Severity Level",
                              value: cveData.severity,
                              tooltip:
                                "Describes how serious the vulnerability is (Low, Medium, High, Critical)",
                            },
                            {
                              key: "summary",
                              label: "Summary",
                              value: cveData.summary,
                              tooltip:
                                "A short description of what the vulnerability is and how it affects the system.",
                            },
                            {
                              key: "references",
                              label: "References",
                              value: cveData.references,
                              tooltip:
                                "Links to official reports, security advisories, or research papers",
                            },
                            {
                              key: "migration_patches",
                              label: "Migration & Patches",
                              value: cveData.migration_patches,
                              tooltip:
                                "Steps or updates provided by vendors to fix or reduce the risk of the vulnerability.",
                            },
                            {
                              key: "exploits",
                              label: "Exploits",
                              value: cveData.exploits,
                              tooptip:
                                "Code or methods hackers can use to take advantage of the vulnerability.",
                            },
                            {
                              key: "vector",
                              label: "Vector",
                              value: cveData.vector,
                              tooltip:
                                "Explains how the attack works (e.g., local access, network attack, etc.)",
                            },
                            {
                              key: "cwe_id",
                              label: "CWE ID",
                              value: cveData.cwe_id,
                              tooltip:
                                "A category that classifies the type of vulnerability.",
                            },
                          ].map(({ key, label, value, tooltip }) => {
                            if (
                              value === null ||
                              value === undefined ||
                              (Array.isArray(value) && value.length === 0) ||
                              value === ""
                            ) {
                              return null;
                            }

                            return (
                              <BulkCustomAccordion
                                key={key}
                                expanded={
                                  state.expandedAccordions[index]?.[key] ||
                                  false
                                }
                                onChange={() =>
                                  handleBulkAccordionChange(index, key)
                                }
                              >
                                <BulkCustomAccordionSummary
                                  expandIcon={
                                    <IconWrapper
                                      src={
                                        state.expandedAccordions[index]?.[key]
                                          ? MinusIcon
                                          : ExpandMoreIcon
                                      }
                                      alt="Toggle"
                                    />
                                  }
                                >
                                  <BulkCustomTypography
                                    expanded={
                                      state.expandedAccordions[index]?.[key]
                                    }
                                  >
                                    <Tooltip title={tooltip} placement="top">
                                      {label}
                                    </Tooltip>
                                  </BulkCustomTypography>
                                </BulkCustomAccordionSummary>
                                <BulkCustomAccordionDetails>
                                  {Array.isArray(value) &&
                                  key === "references" ? (
                                    <ReferencesContainer>
                                      {value.map(
                                        (ref, idx) =>
                                          ref.url && (
                                            <ReferenceItem key={idx}>
                                              <ReferenceLink
                                                href={ref.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {ref.url}
                                              </ReferenceLink>
                                            </ReferenceItem>
                                          )
                                      )}
                                    </ReferencesContainer>
                                  ) : Array.isArray(value) &&
                                    key === "exploits" ? (
                                    <div>
                                      {value.map((exploit, idx) => (
                                        <AccordionSummaryValues
                                          key={idx}
                                          variant="body2"
                                        >
                                          {exploit.value}
                                        </AccordionSummaryValues>
                                      ))}
                                    </div>
                                  ) : (
                                    <AccordionSummaryValues>
                                      {value}
                                    </AccordionSummaryValues>
                                  )}
                                </BulkCustomAccordionDetails>
                              </BulkCustomAccordion>
                            );
                          })}
                        </Collapse>
                      </TableCell>
                    </TableRow> */}
                                            </React.Fragment>
                                        )
                                    )


                                })
                            }

                        </TableBody>
                    </StyledTable>
                </CenteredTableContainer>
                {
                    paginatedResults && paginatedResults?.length > 0 &&
                    (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePaginationChange}
                                renderItem={(item) => (
                                    <PaginationItem
                                        slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                                        {...item}
                                        sx={{
                                            color: "#fff",
                                            borderColor: "#fff",

                                            "&.Mui-selected": {
                                                backgroundColor: "#fff",
                                                color: "#000",
                                                "&:hover": {
                                                    backgroundColor: "#e0e0e0"
                                                },
                                            },

                                            "&.MuiSvgIcon-root": {
                                                color: "#fff"
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )
                }
            </>


        </div>
    );
};

export default Reports;
